import { useCallback, useRef, useState } from 'react'
import { MAX_CONTEXT_UPLOAD_FILES } from '~/components/chat/AttachUploadButton'
import { uploadFile, uploadMetadataFile } from '~/services/organizationService'
import type {
  ContextUploadApiItem,
  ContextUploadItem,
  UploadStatus,
  UploadedDirectory,
  UploadedFile
} from '~/types/contextUpload'
import { BetterToast } from '~/utils/toast'
import { useFolderPicker } from './useFolderPicker'

/**
 * Allowed file extensions for context uploads.
 * Must match backend validation in upload.py (extra_uploaded_context)
 */
export const ALLOWED_CONTEXT_UPLOAD_EXTENSIONS = new Set([
  // Documents
  '.txt',
  '.md',
  '.pdf',
  '.csv',
  '.docx',
  '.xlsx',
  '.pptx',
  '.xls',
  // Web/Markup
  '.html',
  '.htm',
  '.xml',
  '.xsd',
  '.wsdl',
  // Images
  '.png',
  '.jpg',
  '.jpeg',
  '.webp',
  '.heic',
  '.heif',
  // API/Data formats
  '.json',
  '.jsonl',
  '.yaml',
  '.yml',
  '.raml',
  '.oas',
  '.proto',
  // Code files
  '.java',
  '.py',
  '.js',
  '.groovy',
  '.sh',
  '.sql',
  '.dwl',
  '.dw',
  '.mel',
  // Config/Properties
  '.properties',
  '.pem',
  '.mf',
  '.descriptor',
  '.template',
  // Testing/Integration
  '.feature',
  '.multipart',
  '.hl7',
  '.drl',
  // Logs/Fragments
  '.log',
  '.frag',
  // Videos
  '.mp4',
  '.avi',
  '.mov',
  '.mkv',
  '.webm',
  '.m4v',
  '.wmv'
])

/**
 * Check if a file extension is allowed for context uploads
 */
export const isAllowedFileExtension = (filename: string): boolean => {
  const lastDotIndex = filename.lastIndexOf('.')
  if (lastDotIndex === -1) return false
  const extension = filename.slice(lastDotIndex).toLowerCase()
  return ALLOWED_CONTEXT_UPLOAD_EXTENSIONS.has(extension)
}

/**
 * Get file extension from filename
 */
const getFileExtension = (filename: string): string => {
  const lastDotIndex = filename.lastIndexOf('.')
  if (lastDotIndex === -1) return ''
  return filename.slice(lastDotIndex).toLowerCase()
}

// Helper function to format file size
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
}

export interface UseContextUploadOptions {
  /** Called when an upload completes (optional) */
  onUploadComplete?: (upload: ContextUploadItem) => void
  /** Called when an upload fails (optional) */
  onUploadError?: (upload: ContextUploadItem, error: string) => void
}

export interface UseContextUploadReturn {
  /** Array of current context uploads */
  contextUploads: ContextUploadItem[]
  /** Handle file selection (multiple individual files) */
  handleFilesUpload: (files: FileList) => Promise<void>
  /** Handle folder selection (unified: works in both browser and plugin) */
  handleFolderUpload: () => Promise<void>
  /** Remove a context upload by ID */
  handleRemoveContextUpload: (uploadId: number) => void
  /** Clear all uploads */
  clearUploads: () => void
  /** Get context upload data in API format (only completed uploads) */
  getContextUploadData: () => ContextUploadApiItem[]
  /** Check if any uploads are pending or in progress */
  hasPendingUploads: boolean
  /** Check if there are any uploads (including completed) */
  hasUploads: boolean
}

/**
 * Hook to manage context uploads (files and directories) for chat messages.
 * Handles S3 upload, progress tracking, and conversion to API format.
 *
 * @param workspaceUuid - The workspace UUID for uploads
 * @param options - Optional callbacks for upload events
 */
export function useContextUpload(
  workspaceUuid: string | undefined,
  options: UseContextUploadOptions = {}
): UseContextUploadReturn {
  const [contextUploads, setContextUploads] = useState<ContextUploadItem[]>([])
  const { pickFolder } = useFolderPicker()

  // Upload context (file/directory) to S3 and update progress
  const uploadContextToS3 = useCallback(
    async (uploadId: number, fileToUpload: File, uploadType: 'file' | 'directory') => {
      if (!workspaceUuid) {
        setContextUploads(prev =>
          prev.map(item =>
            item.id === uploadId
              ? { ...item, uploadStatus: 'error' as UploadStatus, error: 'No workspace selected' }
              : item
          )
        )
        return
      }

      // Mark as uploading
      setContextUploads(prev =>
        prev.map(item =>
          item.id === uploadId
            ? { ...item, uploadStatus: 'uploading' as UploadStatus, uploadProgress: 0 }
            : item
        )
      )

      // Choose the appropriate upload function based on type
      const uploadFn = uploadType === 'file' ? uploadMetadataFile : uploadFile

      try {
        await uploadFn(workspaceUuid, fileToUpload, {
          onProgress: (progress: number) => {
            setContextUploads(prev =>
              prev.map(item =>
                item.id === uploadId ? { ...item, uploadProgress: Math.round(progress) } : item
              )
            )
          },
          onComplete: (s3Path: string) => {
            setContextUploads(prev => {
              const updated = prev.map(item =>
                item.id === uploadId
                  ? {
                      ...item,
                      uploadStatus: 'completed' as UploadStatus,
                      s3Path,
                      uploadProgress: 100,
                      // Clear file/tarFile to free memory
                      ...(item.type === 'file' ? { file: undefined } : { tarFile: undefined })
                    }
                  : item
              )
              // Call onUploadComplete callback if provided
              const completedUpload = updated.find(u => u.id === uploadId)
              if (completedUpload && options.onUploadComplete) {
                options.onUploadComplete(completedUpload)
              }
              return updated
            })
          },
          onError: (error: string) => {
            setContextUploads(prev => {
              const updated = prev.map(item =>
                item.id === uploadId
                  ? { ...item, uploadStatus: 'error' as UploadStatus, error }
                  : item
              )
              // Call onUploadError callback if provided
              const failedUpload = updated.find(u => u.id === uploadId)
              if (failedUpload && options.onUploadError) {
                options.onUploadError(failedUpload, error)
              }
              return updated
            })
            BetterToast.error(`Upload failed: ${error}`)
          }
        })
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : 'Upload failed'
        setContextUploads(prev => {
          const updated = prev.map(item =>
            item.id === uploadId
              ? { ...item, uploadStatus: 'error' as UploadStatus, error: errorMsg }
              : item
          )
          const failedUpload = updated.find(u => u.id === uploadId)
          if (failedUpload && options.onUploadError) {
            options.onUploadError(failedUpload, errorMsg)
          }
          return updated
        })
        BetterToast.error(`Upload failed: ${errorMsg}`)
      }
    },
    [workspaceUuid, options]
  )

  // Counter for generating unique IDs
  const uploadIdCounter = useRef(0)

  // Ref to track current upload count (avoids race conditions with state)
  const uploadCountRef = useRef(0)

  // Handle files upload (individual files)
  const handleFilesUpload = useCallback(
    async (files: FileList) => {
      const rejectedFiles: string[] = []
      let uploadedCount = 0

      // Get current upload count from ref (more reliable than state for immediate checks)
      const currentCount = uploadCountRef.current

      // Calculate how many files we can still accept
      const remainingSlots = MAX_CONTEXT_UPLOAD_FILES - currentCount

      if (remainingSlots <= 0) {
        BetterToast.error(`Maximum ${MAX_CONTEXT_UPLOAD_FILES} files allowed`)
        return
      }

      for (let i = 0; i < files.length; i++) {
        const file = files[i]

        // Check if we've reached the max limit
        if (uploadedCount >= remainingSlots) {
          const skippedCount = files.length - i
          BetterToast.error(
            `Limit reached: ${skippedCount} file(s) were not added. Maximum ${MAX_CONTEXT_UPLOAD_FILES} files allowed.`
          )
          break
        }

        // Validate file extension
        if (!isAllowedFileExtension(file.name)) {
          const ext = getFileExtension(file.name) || 'no extension'
          rejectedFiles.push(`${file.name} (${ext})`)
          continue
        }

        const uploadId = ++uploadIdCounter.current

        const newUpload: UploadedFile = {
          type: 'file',
          id: uploadId,
          name: file.name,
          size: formatFileSize(file.size),
          file: file,
          uploadStatus: 'pending'
        }

        setContextUploads(prev => [...prev, newUpload])
        uploadCountRef.current++ // Immediately update ref to prevent race conditions
        uploadedCount++

        // Start upload immediately using uploadMetadataFile
        uploadContextToS3(uploadId, file, 'file')
      }

      // Show toast for rejected files
      if (rejectedFiles.length > 0) {
        const message =
          rejectedFiles.length === 1
            ? `File type not supported: ${rejectedFiles[0]}`
            : `${rejectedFiles.length} files have unsupported types: ${rejectedFiles.slice(0, 3).join(', ')}${rejectedFiles.length > 3 ? '...' : ''}`
        BetterToast.error(message)
      }
    },
    [uploadContextToS3]
  )

  // Handle folder upload (unified: browser opens file dialog, plugin opens native picker)
  const handleFolderUpload = useCallback(async () => {
    if (uploadCountRef.current >= MAX_CONTEXT_UPLOAD_FILES) {
      BetterToast.error(`Maximum ${MAX_CONTEXT_UPLOAD_FILES} files allowed`)
      return
    }

    try {
      const result = await pickFolder('Select a folder to upload')
      if (!result) return

      const { tarFile, folderName, originalFilename } = result

      const uploadId = ++uploadIdCounter.current
      const newUpload: UploadedDirectory = {
        type: 'directory',
        id: uploadId,
        name: folderName,
        size: formatFileSize(tarFile.size),
        originalFilename,
        tarFile,
        uploadStatus: 'pending'
      }

      setContextUploads(prev => [...prev, newUpload])
      uploadCountRef.current++

      uploadContextToS3(uploadId, tarFile, 'directory')
    } catch (error) {
      BetterToast.error(
        `Failed to process folder: ${error instanceof Error ? error.message : 'Unknown error'}`
      )
    }
  }, [pickFolder, uploadContextToS3])

  // Remove a context upload
  const handleRemoveContextUpload = useCallback((uploadId: number) => {
    setContextUploads(prev => {
      const filtered = prev.filter(item => item.id !== uploadId)
      uploadCountRef.current = filtered.length // Keep ref in sync
      return filtered
    })
  }, [])

  // Clear all uploads
  const clearUploads = useCallback(() => {
    setContextUploads([])
    uploadCountRef.current = 0 // Reset ref
  }, [])

  // Get context upload data in API format (only completed uploads with s3Path)
  const getContextUploadData = useCallback((): ContextUploadApiItem[] => {
    return contextUploads
      .filter(upload => upload.uploadStatus === 'completed' && upload.s3Path)
      .map(upload => ({
        file_upload:
          upload.type === 'file' ? { file_name: upload.name, s3_path: upload.s3Path! } : undefined,
        directory_upload:
          upload.type === 'directory'
            ? { directory_name: upload.name, s3_path: upload.s3Path! }
            : undefined
      }))
  }, [contextUploads])

  // Check if any uploads are pending or in progress
  const hasPendingUploads = contextUploads.some(
    upload => upload.uploadStatus === 'pending' || upload.uploadStatus === 'uploading'
  )

  // Check if there are any uploads
  const hasUploads = contextUploads.length > 0

  return {
    contextUploads,
    handleFilesUpload,
    handleFolderUpload,
    handleRemoveContextUpload,
    clearUploads,
    getContextUploadData,
    hasPendingUploads,
    hasUploads
  }
}

export default useContextUpload
